import { useEffect, useMemo, useState } from 'react';
import {
  MAX_RISKS_PER_CATEGORY,
  RISK_CATEGORY_KEYS,
  RISK_CATEGORY_LABELS,
  criticiteCssClass,
  criticiteFromC,
  emptyRisksMap,
  migrateRisksMap,
  newRiskId,
  onlyPertinent,
  withComputedC,
  type CriticalityMap,
  type MeasuresMap,
  type RiskCategoryKey,
  type RiskCriticalityItem,
  type RiskItem,
  type RiskMeasureItem,
  type RisksMap,
} from '@isbd/shared';
import { invokeAiFunction, kuunda, kuundaErrorMessage } from '@/lib/kuunda';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';
import './risks.css';

type RisksStep = 1 | 2 | 3;

function normalizeIdentified(raw: Record<string, { titre?: string; description?: string }[]>): RisksMap {
  const map = emptyRisksMap();
  let idx = 1;
  for (const key of RISK_CATEGORY_KEYS) {
    const list = Array.isArray(raw[key]) ? raw[key]! : [];
    map[key] = list.slice(0, MAX_RISKS_PER_CATEGORY).map((item) => ({
      id: newRiskId(idx++),
      titre: item.titre ?? '',
      description: item.description ?? '',
      category: key,
      pertinent: null,
    }));
  }
  return map;
}

function toCriticality(map: RisksMap, raw?: Record<string, Partial<RiskCriticalityItem>[]>): CriticalityMap {
  const pertinent = onlyPertinent(map);
  const out = {} as CriticalityMap;
  for (const key of RISK_CATEGORY_KEYS) {
    const base = pertinent[key] ?? [];
    const scored = raw?.[key] ?? [];
    out[key] = base.map((item, i) => {
      const extra = scored.find((s) => s.id === item.id) ?? scored[i] ?? {};
      return withComputedC({
        ...item,
        causes: String(extra.causes ?? ''),
        consequences: String(extra.consequences ?? ''),
        P: Number(extra.P) || 3,
        I: Number(extra.I) || 3,
      });
    });
  }
  return out;
}

function toMeasures(
  criticality: CriticalityMap,
  raw?: Record<string, Partial<RiskMeasureItem>[]>,
): MeasuresMap {
  const out = {} as MeasuresMap;
  for (const key of RISK_CATEGORY_KEYS) {
    const base = criticality[key] ?? [];
    const extras = raw?.[key] ?? [];
    out[key] = base.map((item, i) => {
      const extra = extras.find((s) => s.id === item.id) ?? extras[i] ?? {};
      const plan =
        extra.plan_maitrise ||
        [extra.mesures_preventives, extra.mesures_contingence].filter(Boolean).join('\n') ||
        '';
      return {
        ...item,
        plan_maitrise: plan,
        mesures_preventives: extra.mesures_preventives ?? '',
        mesures_contingence: extra.mesures_contingence ?? '',
      };
    });
  }
  return out;
}

interface Props {
  projectId: string;
}

export function RisksWorkbench({ projectId }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [step, setStep] = useState<RisksStep>(1);
  const [editing, setEditing] = useState(true);
  const [risks, setRisks] = useState<RisksMap>(emptyRisksMap);
  const [criticality, setCriticality] = useState<CriticalityMap | null>(null);
  const [measures, setMeasures] = useState<MeasuresMap | null>(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  const flatIdentified = useMemo(
    () => RISK_CATEGORY_KEYS.flatMap((k) => risks[k] ?? []),
    [risks],
  );
  const pertinentCount = useMemo(
    () => flatIdentified.filter((r) => r.pertinent === true).length,
    [flatIdentified],
  );

  useEffect(() => {
    if (!projectId) return;
    void kuunda
      .from('risk_analyses')
      .select('*')
      .eq('project_id', projectId)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) return;
        const r = migrateRisksMap(data.risks as Record<string, unknown>);
        setRisks(r);
        if (data.criticality && Object.keys(data.criticality).length) {
          setCriticality(data.criticality as CriticalityMap);
        }
        if (data.measures && Object.keys(data.measures).length) {
          setMeasures(data.measures as MeasuresMap);
        }
        setValidated(Boolean(data.is_validated));
        setEditing(!data.is_validated);
        if (data.is_validated) setStep(3);
        else if (data.measures && Object.keys(data.measures).length) setStep(3);
        else if (data.criticality && Object.keys(data.criticality).length) setStep(2);
      });
  }, [projectId]);

  async function persist(patch: Record<string, unknown>) {
    if (!profile) return;
    const { error: err } = await kuunda.from('risk_analyses').upsert(
      {
        user_id: profile.id,
        project_id: projectId,
        ...patch,
      },
      { onConflict: 'project_id' },
    );
    if (err) throw err;
  }

  async function identify() {
    setLoading(true);
    try {
      const result = await invokeAiFunction<{ risks: Record<string, { titre: string; description: string }[]> }>(
        'ai-analyze',
        { kind: 'risks', project_id: projectId },
      );
      const normalized = normalizeIdentified(result.risks ?? {});
      setRisks(normalized);
      setCriticality(null);
      setMeasures(null);
      setValidated(false);
      await persist({ risks: normalized, criticality: {}, measures: {}, is_validated: false, validated_at: null });
      toast.success('Risques proposés — validez la pertinence de chacun (max 3 / catégorie).');
      setStep(1);
      setEditing(true);
    } catch (e) {
      toast.error(kuundaErrorMessage(e, 'Identification impossible'));
    } finally {
      setLoading(false);
    }
  }

  async function saveIdentified() {
    try {
      await persist({ risks });
      toast.success('Identification enregistrée.');
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    }
  }

  async function analyze() {
    if (pertinentCount === 0) {
      toast.error('Marquez au moins un risque comme pertinent avant l’analyse.');
      return;
    }
    setLoading(true);
    try {
      const pertinent = onlyPertinent(risks);
      const result = await invokeAiFunction<{ criticality: Record<string, Partial<RiskCriticalityItem>[]> }>(
        'ai-analyze',
        { kind: 'risks_criticity', project_id: projectId, risks: pertinent },
      );
      const crit = toCriticality(risks, result.criticality);
      setCriticality(crit);
      await persist({ risks, criticality: crit });
      toast.success('Analyse prête — causes, conséquences et criticité (modifiables).');
      setStep(2);
      setEditing(true);
    } catch (e) {
      toast.error(kuundaErrorMessage(e, 'Analyse impossible'));
    } finally {
      setLoading(false);
    }
  }

  async function saveCriticality() {
    if (!criticality) return;
    try {
      await persist({ criticality });
      toast.success('Analyse de criticité enregistrée — passez au plan de maîtrise.');
      setStep(3);
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    }
  }

  async function determineMeasures() {
    if (!criticality) return;
    setLoading(true);
    try {
      const result = await invokeAiFunction<{ measures: Record<string, Partial<RiskMeasureItem>[]> }>(
        'ai-analyze',
        { kind: 'risks_measures', project_id: projectId, criticality },
      );
      const m = toMeasures(criticality, result.measures);
      setMeasures(m);
      await persist({ measures: m });
      toast.success('Plan de maîtrise proposé — amendez puis validez.');
      setStep(3);
      setEditing(true);
    } catch (e) {
      toast.error(kuundaErrorMessage(e, 'Plan de maîtrise impossible'));
    } finally {
      setLoading(false);
    }
  }

  async function saveMeasures() {
    if (!measures) return;
    try {
      await persist({ measures });
      toast.success('Plan de maîtrise enregistré.');
      setEditing(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Enregistrement impossible');
    }
  }

  async function validateAll() {
    try {
      await persist({
        risks,
        criticality: criticality ?? {},
        measures: measures ?? {},
        is_validated: true,
        validated_at: new Date().toISOString(),
      });
      setValidated(true);
      setEditing(false);
      toast.success('Maîtrise des risques validée.');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Validation impossible');
    }
  }

  function updateIdentified(id: string, patch: Partial<RiskItem>) {
    setRisks((prev) => {
      const next = { ...prev };
      for (const key of RISK_CATEGORY_KEYS) {
        next[key] = (prev[key] ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r));
      }
      return next;
    });
  }

  function setPertinent(id: string, pertinent: boolean) {
    updateIdentified(id, { pertinent });
    setEditing(true);
  }

  function addRisk(category: RiskCategoryKey) {
    setRisks((prev) => {
      if ((prev[category] ?? []).length >= MAX_RISKS_PER_CATEGORY) {
        toast.error(`Maximum ${MAX_RISKS_PER_CATEGORY} risques par catégorie.`);
        return prev;
      }
      const all = RISK_CATEGORY_KEYS.flatMap((k) => prev[k] ?? []);
      const item: RiskItem = {
        id: newRiskId(all.length + 1),
        titre: 'Nouveau risque',
        description: '',
        category,
        pertinent: null,
      };
      return { ...prev, [category]: [...(prev[category] ?? []), item] };
    });
    setEditing(true);
  }

  function removeRisk(id: string) {
    setRisks((prev) => {
      const next = { ...prev };
      for (const key of RISK_CATEGORY_KEYS) {
        next[key] = (prev[key] ?? []).filter((r) => r.id !== id);
      }
      return next;
    });
  }

  function updateCrit(id: string, patch: Partial<RiskCriticalityItem>) {
    if (!criticality) return;
    setCriticality((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const key of RISK_CATEGORY_KEYS) {
        next[key] = (prev[key] ?? []).map((r) => {
          if (r.id !== id) return r;
          const merged = { ...r, ...patch };
          const C = (Number(merged.P) || 1) * (Number(merged.I) || 1);
          return { ...merged, C, criticite: criticiteFromC(C) };
        });
      }
      return next;
    });
  }

  function updateMeasure(id: string, patch: Partial<RiskMeasureItem>) {
    if (!measures) return;
    setMeasures((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      for (const key of RISK_CATEGORY_KEYS) {
        next[key] = (prev[key] ?? []).map((r) => (r.id === id ? { ...r, ...patch } : r));
      }
      return next;
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
        {([1, 2, 3] as RisksStep[]).map((s) => (
          <button
            key={s}
            type="button"
            className={`btn ${step === s ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setStep(s)}
          >
            {s === 1
              ? '1. Identification'
              : s === 2
                ? '2. Analyse & évaluation'
                : '3. Plan de maîtrise'}
          </button>
        ))}
      </div>

      {validated ? <p className="chip ok">Validée</p> : null}

      {step === 1 ? (
        <section className="panel">
          <p className="muted">
            L’IA propose des risques dans 6 catégories (max {MAX_RISKS_PER_CATEGORY} retenus). Indiquez
            pertinent / non pertinent pour chaque ligne.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            <button type="button" className="btn btn-accent" disabled={loading} onClick={() => void identify()}>
              {loading ? 'IA…' : 'Identifier les risques (IA)'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)} disabled={validated}>
              Modifier
            </button>
            <button type="button" className="btn btn-primary" disabled={!editing} onClick={() => void saveIdentified()}>
              Enregistrer
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              disabled={pertinentCount === 0}
              onClick={() => void analyze()}
            >
              Passer à l’analyse ({pertinentCount} pertinents)
            </button>
          </div>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Catégorie</th>
                  <th>Risque</th>
                  <th>Description</th>
                  <th>Pertinence</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {RISK_CATEGORY_KEYS.map((cat) =>
                  (risks[cat] ?? []).map((r) => (
                    <tr key={r.id} className={r.pertinent === false ? 'risk-row-dismissed' : undefined}>
                      <td>{r.id}</td>
                      <td>{RISK_CATEGORY_LABELS[cat]}</td>
                      <td>
                        {editing ? (
                          <input value={r.titre} onChange={(e) => updateIdentified(r.id, { titre: e.target.value })} />
                        ) : (
                          r.titre
                        )}
                      </td>
                      <td>
                        {editing ? (
                          <textarea
                            value={r.description}
                            onChange={(e) => updateIdentified(r.id, { description: e.target.value })}
                          />
                        ) : (
                          r.description
                        )}
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className={`btn ${r.pertinent === true ? 'btn-risk-pertinent' : 'btn-ghost'}`}
                            disabled={validated}
                            onClick={() => setPertinent(r.id, true)}
                          >
                            Pertinent
                          </button>
                          <button
                            type="button"
                            className={`btn ${r.pertinent === false ? 'btn-risk-non-pertinent' : 'btn-ghost'}`}
                            disabled={validated}
                            onClick={() => setPertinent(r.id, false)}
                          >
                            Non pertinent
                          </button>
                        </div>
                      </td>
                      <td>
                        {editing ? (
                          <button type="button" className="btn btn-ghost" onClick={() => removeRisk(r.id)}>
                            ✕
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  )),
                )}
              </tbody>
            </table>
          </div>
          {editing ? (
            <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>
              {RISK_CATEGORY_KEYS.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className="btn btn-ghost"
                  disabled={(risks[cat] ?? []).length >= MAX_RISKS_PER_CATEGORY}
                  onClick={() => addRisk(cat)}
                >
                  + {RISK_CATEGORY_LABELS[cat]}
                </button>
              ))}
            </div>
          ) : null}
        </section>
      ) : null}

      {step === 2 ? (
        <section className="panel">
          {!criticality ? (
            <button type="button" className="btn btn-accent" disabled={loading} onClick={() => void analyze()}>
              Lancer l’analyse et l’évaluation
            </button>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)} disabled={validated}>
                  Modifier
                </button>
                <button type="button" className="btn btn-primary" disabled={!editing} onClick={() => void saveCriticality()}>
                  Enregistrer
                </button>
                <button type="button" className="btn btn-ghost" onClick={() => void determineMeasures()}>
                  Plan de maîtrise
                </button>
              </div>
              <div className="risk-legend">
                <span className="risk-crit-critique">Critique</span>
                <span className="risk-crit-eleve">Élevé</span>
                <span className="risk-crit-modere">Modéré</span>
                <span className="risk-crit-faible">Faible</span>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Risque</th>
                      <th>Causes possibles</th>
                      <th>Conséquences possibles</th>
                      <th>P (Probabilité)</th>
                      <th>I (Impact)</th>
                      <th>C</th>
                      <th>Criticité</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RISK_CATEGORY_KEYS.flatMap((cat) =>
                      (criticality[cat] ?? []).map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{r.titre}</td>
                          <td>
                            {editing ? (
                              <textarea
                                value={r.causes}
                                onChange={(e) => updateCrit(r.id, { causes: e.target.value })}
                              />
                            ) : (
                              r.causes
                            )}
                          </td>
                          <td>
                            {editing ? (
                              <textarea
                                value={r.consequences}
                                onChange={(e) => updateCrit(r.id, { consequences: e.target.value })}
                              />
                            ) : (
                              r.consequences
                            )}
                          </td>
                          <td>
                            {editing ? (
                              <input
                                type="number"
                                min={1}
                                max={5}
                                value={r.P}
                                onChange={(e) => updateCrit(r.id, { P: Number(e.target.value) })}
                              />
                            ) : (
                              r.P
                            )}
                          </td>
                          <td>
                            {editing ? (
                              <input
                                type="number"
                                min={1}
                                max={5}
                                value={r.I}
                                onChange={(e) => updateCrit(r.id, { I: Number(e.target.value) })}
                              />
                            ) : (
                              r.I
                            )}
                          </td>
                          <td>{r.C}</td>
                          <td>
                            <span className={`risk-badge ${criticiteCssClass(r.criticite)}`}>{r.criticite}</span>
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      ) : null}

      {step === 3 ? (
        <section className="panel">
          {!measures ? (
            <button
              type="button"
              className="btn btn-accent"
              disabled={loading || !criticality}
              onClick={() => void determineMeasures()}
            >
              Générer le plan de maîtrise (IA)
            </button>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-ghost" onClick={() => setEditing(true)} disabled={validated}>
                  Modifier
                </button>
                <button type="button" className="btn btn-primary" disabled={!editing} onClick={() => void saveMeasures()}>
                  Enregistrer
                </button>
                <button type="button" className="btn btn-accent" disabled={validated} onClick={() => void validateAll()}>
                  Valider l’analyse
                </button>
              </div>
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Risque</th>
                      <th>Criticité</th>
                      <th>Plan de maîtrise</th>
                    </tr>
                  </thead>
                  <tbody>
                    {RISK_CATEGORY_KEYS.flatMap((cat) =>
                      (measures[cat] ?? []).map((r) => (
                        <tr key={r.id}>
                          <td>{r.id}</td>
                          <td>{r.titre}</td>
                          <td>
                            <span className={`risk-badge ${criticiteCssClass(r.criticite)}`}>{r.criticite}</span>
                          </td>
                          <td>
                            {editing ? (
                              <textarea
                                value={r.plan_maitrise}
                                onChange={(e) => updateMeasure(r.id, { plan_maitrise: e.target.value })}
                              />
                            ) : (
                              r.plan_maitrise
                            )}
                          </td>
                        </tr>
                      )),
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      ) : null}
    </div>
  );
}
